import { useAuth0 } from "@auth0/auth0-react";
import styles from "./income-category.module.css";
import Loader from "../../../components/Loader";
import Footer from "../../../components/Footer";
import { ArrowLeft, Home, X } from "react-feather";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Slide, Snackbar, TablePagination, Typography } from "@mui/material";
import { TableLoaderSmall } from "../../../components/LoaderC";
import { Helmet } from "react-helmet";
import { useEffect, useRef, useState } from "react";
import AddIncomeCategory from "./components/AddIncomeCategory";
import axios from "axios";
import { toast } from "react-smart-toaster";
import IncomeCategoryFilter from "./components/IncomeCategoryFilter";
import IncomeCategoryCard from "./components/IncomeCategoryCard";
import withCustomAuthenticationRequired from "../../../auth/withCustomAuthenticationRequired";

const IncomeCategory = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [searchKey, setSearchKey] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const latestRequest = useRef(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [filterData, setFilterData] = useState({ searchKey: "", type: "" });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(8);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectecId, setSelectecId] = useState(0);
  const [editUserData, setEditUserData] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Debounce search so we don't hit the API on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setSearchKey(filterData.searchKey), filterData.searchKey ? 400 : 0);
    return () => clearTimeout(timer);
  }, [filterData.searchKey]);

  useEffect(() => {
    const requestId = ++latestRequest.current;

    const getCategoryList = async () => {
      try {
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        const response = await axios.get(process.env.REACT_APP_PAYMENT_API_URL + "v1/income-categories-paginated", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            page: page + 1,
            per_page: perPage,
            search: searchKey.trim() || undefined,
          },
        });

        // Ignore responses from requests that were superseded by newer filters/pages
        if (requestId !== latestRequest.current) return;

        setFilteredData(response.data?.data || []);
        setTotalCount(response.data?.meta?.total || 0);
        setLoadError(false);
      } catch (error) {
        if (requestId !== latestRequest.current) return;
        console.error("[IncomeCategory] Failed to load category list", error);
        setLoadError(true);
        toast.error(error?.response?.data?.message || "Unable to load income categories. Please try again.");
      } finally {
        if (requestId === latestRequest.current)
          setContentLoading(false);
      }
    };

    getCategoryList();
  }, [getAccessTokenSilently, page, perPage, searchKey, reloadKey]);

  // Reload from the server so a new category lands on the correct page and the total stays accurate
  const reloadList = () => setReloadKey((prev) => prev + 1);

  const confirmDelete = async () => {
    setDeleteModalOpen(false);
    setLoading(true);
    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

    axios.delete(process.env.REACT_APP_PAYMENT_API_URL + "v1/income-categories/" + selectecId, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then((response) => {
      setSnackbarOpen(true);
      setSnackbarMsg(response.data.message);
      setLoading(false);

      setSelectecId(0);

      // Step back a page when the last card on it was deleted, otherwise refill the current page
      if (filteredData.length === 1 && page > 0) setPage(page - 1);
      else reloadList();
    }).catch((error) => {
      if (typeof error.response !== "undefined")
        toast.error(error.response.data.message);
      setLoading(false);
    });
  };

  const renderPagination = () => (<TablePagination
    count={totalCount}
    color="primary"
    page={page}
    rowsPerPage={perPage}
    onPageChange={(e, v) => setPage(v)}
    rowsPerPageOptions={[
      { label: "4", value: 4 },
      { label: "8", value: 8 },
      { label: "16", value: 16 },
      { label: "50", value: 50 },
    ]}
    onRowsPerPageChange={(e) => {
      setPage(0);
      setPerPage(e.target.value);
    }}
    labelRowsPerPage={"Categories per Page:"}
  />);

  const renderEmpty = () => (loadError ? (<div className={`${styles.NoDataMain}`}>
    <p className={`${styles.NoDataText}`}>Something went wrong</p>
    <p className={`${styles.NoDataTextSub}`}>We couldn't load income categories.</p>
    <button type="button" className={`${styles.RetryBu}`} onClick={reloadList}>Retry</button>
  </div>) : (<div className={`${styles.NoDataMain}`}>
    <div className={`${styles.NoDataIMG}`}>
      <img src="/static/img/no-prospect.png" alt="No Data" />
    </div>
    <p className={`${styles.NoDataText}`}>Let's get started!</p>
    <p className={`${styles.NoDataTextSub}`}>You don't have any income category saved.</p>
  </div>));

  return (<>
    {loading && <Loader />}
    <Helmet title="Income Categories" />

    <div className={`${styles.pageTitleWrap}`}>
      <Typography display="inline" className={`${styles.pageTitle}`}>Income Category</Typography>
      <nav aria-label="breadcrumb">
        <ol className={`${styles.breadcrumb}`}>
          <li className={`${styles.breadcrumbItem}`}><span><Home /></span>Income</li>
          <li className={`${styles.breadcrumbItem} ${styles.active}`}>Income Category</li>
        </ol>
      </nav>
    </div>

    <div className={`${styles.BodyCon}`}>
      <div className={`${styles.mainCardDiv}`}>
        <div className={`${styles.mainCard}`}>
          {contentLoading && (<div className="Loader TableLoader">
            <TableLoaderSmall />
          </div>)}

          {!contentLoading && (<div className="control-pane">
            <div className={`${styles.Row}`}>
              <IncomeCategoryFilter setFilterData={setFilterData.bind(this)} setAddModalOpen={setAddModalOpen.bind(this)} setPage={setPage.bind(this)} />

              <div className={`${styles.NotesRow}`}>
                {filteredData.map((item) => {
                  return (<IncomeCategoryCard
                    key={item?.id}
                    item={item}
                    setDeleteModalOpen={setDeleteModalOpen.bind(this)}
                    setSelectecId={setSelectecId.bind(this)}
                    setEditUserData={setEditUserData.bind(this)}
                    setEditModalOpen={setEditModalOpen.bind(this)}
                  />);
                })}

                {filteredData.length > 0 && (<div className="Pagination TablePaginationNew List">
                  {renderPagination()}
                </div>)}

                {filteredData.length === 0 && renderEmpty()}
              </div>
            </div>
          </div>)}
        </div>
      </div>
    </div>

    <Dialog
      open={addModalOpen}
      onClose={(e) => { setAddModalOpen(false); }}
      fullWidth
      maxWidth="sm"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp MobileForm"
    >
      <DialogTitle id="alert-dialog-title">
        <button className={`${styles.BackAddBU}`} onClick={(e) => { setAddModalOpen(false); }}>
          <ArrowLeft />
        </button>
        Add Category
        <button onClick={(e) => { setAddModalOpen(false); }}>
          <X />
        </button>
      </DialogTitle>
      <DialogContent className={`${styles.OverflowUnset}`}>
        <AddIncomeCategory
          setAddModalOpen={setAddModalOpen.bind(this)}
          setLoading={setLoading.bind(this)}
          setSnackbarOpen={setSnackbarOpen.bind(this)}
          setSnackbarMsg={setSnackbarMsg.bind(this)}
          setAllData={reloadList}
          editData={null}
        />
      </DialogContent>
    </Dialog>

    <Dialog
      open={editModalOpen}
      onClose={(e) => { setEditModalOpen(false); }}
      fullWidth
      maxWidth="sm"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp MobileForm"
    >
      <DialogTitle id="alert-dialog-title">
        <button className={`${styles.BackAddBU}`} onClick={(e) => { setEditModalOpen(false); }}>
          <ArrowLeft />
        </button>
        Update Category
        <button onClick={(e) => { setEditModalOpen(false); }}>
          <X />
        </button>
      </DialogTitle>
      <DialogContent className={`${styles.OverflowUnset}`}>
        <AddIncomeCategory
          setAddModalOpen={setEditModalOpen.bind(this)}
          setLoading={setLoading.bind(this)}
          setSnackbarOpen={setSnackbarOpen.bind(this)}
          setSnackbarMsg={setSnackbarMsg.bind(this)}
          setAllData={setFilteredData.bind(this)}
          editData={editUserData}
        />
      </DialogContent>
    </Dialog>

    <Dialog
      open={deleteModalOpen}
      onClose={(e) => setDeleteModalOpen(false)}
      fullWidth
      maxWidth="sm"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp"
    >
      <DialogTitle id="alert-dialog-title">
        Would you like to delete this category?
        <button onClick={(e) => setDeleteModalOpen(false)}>
          <X />
        </button>
      </DialogTitle>
      <DialogContent>
        <div className="ModalFormGroup">
          <label className="PopupBodyText">The category information will be removed immediately.You can't undo this action.</label>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={(e) => confirmDelete()} className="CancelPopupBU">Delete</Button>
        <Button onClick={(e) => setDeleteModalOpen(false)} className="SubmitPopupBU"        >Cancel</Button>
      </DialogActions>
    </Dialog>

    <Snackbar
      open={snackbarOpen}
      onClose={(e) => setSnackbarOpen(false)}
      TransitionComponent={Slide}
      message={snackbarMsg}
      autoHideDuration={10000}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      action={
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={(e) => setSnackbarOpen(false)}
        >
          <X />
        </IconButton>
      }
    />

    <Footer />
  </>);
};

export default withCustomAuthenticationRequired(IncomeCategory, {
  onRedirecting: () => <Loader />,
});
